import React, { useLayoutEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Avatar, Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { toProfilePath } from "../../universal/v2Routes";
import utils from "../../universal/utils";
import { resolveUserAvatarSrc } from "../../utils/avatarUrl";
import { isLeaderboardViewerRow } from "./leaderboardFormat";

const basePath = utils.getEdgeBasePath();

const FIRST_PLACE_PODIUM_BG_URL = "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/backgrounds/leaderboard-first-place-background.png";
const SECOND_PLACE_PODIUM_BG_URL = "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/backgrounds/leaderboard-second-place-background.png";
const THIRD_PLACE_PODIUM_BG_URL = "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/backgrounds/leaderboard-third-place-background.png";

const podiumCardVariants = {
    hidden: {
        opacity: 0,
        y: 28,
        scale: 0.96,
        filter: "blur(10px)",
    },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)",

        transition: {
            duration: 0.85,
            ease: [0.22, 1, 0.36, 1],
        },
    },
};

const ANIMATION_DELAYS = {
    1: 0,
    2: 0.12,
    3: 0.2,
};

const PODIUM_POSITIONS = [
    { id: 2, label: "2" },
    { id: 1, label: "1" },
    { id: 3, label: "3" },
];

const PODIUM_AVATAR_BOX_SIZE = {
    1: { xs: "4.8rem", xl: "5.5rem" },
    2: { xs: "4.5rem", xl: "5rem" },
    3: { xs: "4.2rem", xl: "4.8rem" },
};

/** Shrink desktop podium so 3 cards fit beside the sidebar. */
const PODIUM_COMPACT_MQ = "@media (max-width:1149.95px)";

const PODIUM_COMPACT_WIDTH = {
    1: "10.9rem",
    2: "9.35rem",
    3: "9.35rem",
};

const PODIUM_COMPACT_HEIGHT = {
    1: "15.25rem",
    2: "13.75rem",
    3: "13rem",
};

const PODIUM_COMPACT_AVATAR = {
    1: "4.1rem",
    2: "3.85rem",
    3: "3.6rem",
};

const PODIUM_STYLE = {
    1: {
        height: "18rem",
        zIndex: 3,
        mt: 0,
        accent: "#F4BA43",
        borderColor: "rgba(244,186,67,0.42)",
        background: `radial-gradient(ellipse at 50% 24%, rgba(255,205,94,0.18), transparent 52%), linear-gradient(180deg, rgba(8,8,7,0.05) 0%, rgba(8,8,7,0.23) 55%, rgba(4,4,4,0.76) 100%), url(${FIRST_PLACE_PODIUM_BG_URL}) center top / cover no-repeat`,
        boxShadow: "0 30px 72px rgba(0,0,0,0.66), 0 0 38px rgba(244,186,67,0.27), 0 0 9px rgba(255,225,154,0.13), inset 0 1px 0 rgba(255,235,184,0.4)",
        hoverShadow: "0 38px 90px rgba(0,0,0,0.74), 0 0 49px rgba(244,186,67,0.38), 0 0 12px rgba(255,225,154,0.2), inset 0 1px 0 rgba(255,239,199,0.5)",
        overlay: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent 22%), radial-gradient(circle at 50% 36%, rgba(240,179,62,0.1), transparent 45%)",
        placeBackground: "radial-gradient(circle at 30% 16%, rgba(255,255,255,0.54), transparent 25%), linear-gradient(145deg, #FFE39A 0%, #F4BA43 38%, #D79216 72%, #A9660C 100%)",
        placeBorder: "1px solid rgba(255,221,139,0.76)",
        placeShadow: "0 9px 22px rgba(0,0,0,0.5), 0 0 20px rgba(244,186,67,0.36), inset 0 2px 0 rgba(255,255,255,0.58), inset 0 -6px 10px rgba(91,45,0,0.32)",
        placeText: "#201507",
    },
    2: {
        height: "16.2rem",
        zIndex: 2,
        accent: "#DDE3EA",
        borderColor: "rgba(221,227,234,0.38)",
        background: `radial-gradient(ellipse at 50% 24%, rgba(235,240,247,0.15), transparent 52%), linear-gradient(180deg, rgba(8,8,10,0.07) 0%, rgba(8,8,10,0.27) 55%, rgba(4,4,5,0.77) 100%), url(${SECOND_PLACE_PODIUM_BG_URL}) center top / cover no-repeat`,
        boxShadow: "0 24px 58px rgba(0,0,0,0.54), 0 0 35px rgba(221,227,234,0.19), 0 0 8px rgba(255,255,255,0.08), inset 0 1px 0 rgba(248,250,255,0.32)",
        hoverShadow: "0 30px 72px rgba(0,0,0,0.64), 0 0 44px rgba(221,227,234,0.28), 0 0 10px rgba(255,255,255,0.12), inset 0 1px 0 rgba(250,252,255,0.43)",
        overlay: "linear-gradient(180deg, rgba(255,255,255,0.045), transparent 22%), radial-gradient(circle at 50% 36%, rgba(204,208,216,0.085), transparent 45%)",
        placeBackground: "radial-gradient(circle at 30% 16%, rgba(255,255,255,0.68), transparent 25%), linear-gradient(145deg, #FFFFFF 0%, #E5E9EE 34%, #B9C0C8 70%, #7D858E 100%)",
        placeBorder: "1px solid rgba(255,255,255,0.74)",
        placeShadow: "0 9px 20px rgba(0,0,0,0.48), 0 0 18px rgba(221,227,234,0.3), inset 0 2px 0 rgba(255,255,255,0.76), inset 0 -6px 10px rgba(35,42,50,0.3)",
        placeText: "#1A1D20",
    },
    3: {
        height: "15.2rem",
        zIndex: 1,
        accent: "#E89150",
        borderColor: "rgba(232,145,80,0.38)",
        background: `radial-gradient(ellipse at 50% 24%, rgba(255,175,116,0.15), transparent 52%), linear-gradient(180deg, rgba(9,7,6,0.07) 0%, rgba(9,7,6,0.27) 55%, rgba(4,4,4,0.77) 100%), url(${THIRD_PLACE_PODIUM_BG_URL}) center top / cover no-repeat`,
        boxShadow: "0 22px 54px rgba(0,0,0,0.52), 0 0 35px rgba(232,145,80,0.2), 0 0 8px rgba(255,190,143,0.08), inset 0 1px 0 rgba(255,211,177,0.32)",
        hoverShadow: "0 28px 68px rgba(0,0,0,0.62), 0 0 44px rgba(232,145,80,0.3), 0 0 10px rgba(255,190,143,0.12), inset 0 1px 0 rgba(255,220,194,0.43)",
        overlay: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent 22%), radial-gradient(circle at 50% 36%, rgba(193,132,94,0.09), transparent 45%)",
        placeBackground: "radial-gradient(circle at 30% 16%, rgba(255,245,235,0.56), transparent 25%), linear-gradient(145deg, #FFD3AD 0%, #E89150 34%, #B85E2D 70%, #71351B 100%)",
        placeBorder: "1px solid rgba(255,191,139,0.72)",
        placeShadow: "0 9px 20px rgba(0,0,0,0.48), 0 0 18px rgba(232,145,80,0.32), inset 0 2px 0 rgba(255,240,226,0.62), inset 0 -6px 10px rgba(67,24,8,0.34)",
        placeText: "#291307",
    },
};

export const PODIUM_RANK_BADGE_STYLES = Object.freeze(
    Object.fromEntries(
        Object.entries(PODIUM_STYLE).map(([rank, style]) => [
            rank,
            {
                accent: style.accent,
                background: style.placeBackground,
                border: style.placeBorder,
                boxShadow: style.placeShadow,
                color: style.placeText,
            },
        ]),
    ),
);

const MOBILE_PODIUM_ORDER = [
    { id: 1, label: "1" },
    { id: 2, label: "2" },
    { id: 3, label: "3" },
];

function resolvePodiumAvatarSrc(row, user, avatarCacheBust) {
    if (!row) return null;

    const isViewer = isLeaderboardViewerRow(row, user);
    const avatarUser = isViewer ? { ...row, ...user } : row;

    return resolveUserAvatarSrc({
        basePath,
        user: avatarUser,
        avatarCacheBust: isViewer ? avatarCacheBust : null,
    });
}

function MobilePodiumRow({
    position,
    row,
    user,
    avatarCacheBust,
    history,
    location,
}) {
    const style = PODIUM_STYLE[position.id];
    const showYouLabel = row ? isLeaderboardViewerRow(row, user) : false;
    const profilePath = toProfilePath(row?._id, location.pathname);
    const avatarSrc = resolvePodiumAvatarSrc(row, user, avatarCacheBust);

    return (
        <Box
            data-leaderboard-viewer-me={showYouLabel ? "" : undefined}
            role={profilePath ? "button" : undefined}
            tabIndex={profilePath ? 0 : undefined}
            onClick={profilePath ? () => history.push(profilePath) : undefined}
            onKeyDown={
                profilePath
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              history.push(profilePath);
                          }
                      }
                    : undefined
            }
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.65rem",
                width: "100%",
                px: "0.85rem",
                py: "0.75rem",
                mb: "0.45rem",
                borderRadius: "0.85rem",
                boxSizing: "border-box",
                overflow: "hidden",
                border: `1px solid ${style.borderColor}`,
                background: style.background,
                boxShadow: style.boxShadow,
                cursor: profilePath ? "pointer" : "default",
                isolation: "isolate",
                transition: "transform 200ms ease, box-shadow 200ms ease",
                ...(profilePath
                    ? {
                          "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: style.hoverShadow,
                          },
                          "&:focus-visible": {
                              outline: `2px solid ${alpha(style.accent, 0.7)}`,
                              outlineOffset: "2px",
                          },
                      }
                    : {}),
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: style.overlay,
                    opacity: 0.92,
                    pointerEvents: "none",
                }}
            />
            <Typography
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "1.75rem",
                    height: "1.75rem",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    background: style.placeBackground,
                    border: style.placeBorder,
                    boxShadow: style.placeShadow,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: style.placeText,
                    textAlign: "center",
                    textShadow: "0 1px 0 rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.8)",
                }}
            >
                {position.label}
            </Typography>
            <Avatar
                src={avatarSrc || undefined}
                alt={row?.nickname || "Player"}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    boxShadow: "0 8px 18px rgba(0,0,0,0.5)",
                }}
            />
            <Box sx={{ position: "relative", zIndex: 1, flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.92rem",
                        color: colors.text.primary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 4px rgba(0,0,0,0.65)",
                    }}
                >
                    {row?.nickname || "—"}
                </Typography>
            </Box>
        </Box>
    );
}

export default function LeaderboardPodium({
    podiumPlayersData = [],
    user = null,
    avatarCacheBust = null,
}) {
    const history = useHistory();
    const location = useLocation();

    const podiumAnimateSig = podiumPlayersData
        .map((r) => `${r?.userId ?? ""}:${r?._id ?? ""}`)
        .join("|");

    const [podiumMotionVisible, setPodiumMotionVisible] = useState(false);

    useLayoutEffect(() => {
        if (!podiumAnimateSig) {
            setPodiumMotionVisible(false);
            return;
        }
        setPodiumMotionVisible(false);
        const id = requestAnimationFrame(() => {
            setPodiumMotionVisible(true);
        });
        return () => cancelAnimationFrame(id);
    }, [podiumAnimateSig]);

    return (
        <>
            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    width: "100%",
                    mt: "1rem",
                }}
            >
                {MOBILE_PODIUM_ORDER.map((position) => (
                    <MobilePodiumRow
                        key={position.id}
                        position={position}
                        row={podiumPlayersData[position.id - 1]}
                        user={user}
                        avatarCacheBust={avatarCacheBust}
                        history={history}
                        location={location}
                    />
                ))}
            </Box>

            <Box
            sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "center",
                flexWrap: "nowrap",
                gap: { md: "2rem" },
                mt: "2rem",
                pt: "1.1rem",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflow: "visible",
                [PODIUM_COMPACT_MQ]: {
                    gap: "0.75rem",
                },
            }}
        >
            {PODIUM_POSITIONS.map((position) => {
                const style = PODIUM_STYLE[position.id];
                const row = podiumPlayersData[position.id - 1];
                const showYouLabel = row ? isLeaderboardViewerRow(row, user) : false;
                const profilePath = toProfilePath(row?._id, location.pathname);
                const avatarSrc = resolvePodiumAvatarSrc(
                    row,
                    user,
                    avatarCacheBust,
                );

                return (
                    <motion.div
                        key={position.id}
                        variants={podiumCardVariants}
                        initial="hidden"
                        animate={podiumMotionVisible ? "visible" : "hidden"}
                        transition={{
                            opacity: {
                                duration: 0.85,
                                ease: [0.22, 1, 0.36, 1],
                                delay: ANIMATION_DELAYS[position.id],
                            },
                            y: {
                                duration: 6,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: ANIMATION_DELAYS[position.id],
                            },
                            scale: {
                                duration: 0.85,
                                ease: [0.22, 1, 0.36, 1],
                                delay: ANIMATION_DELAYS[position.id],
                            },
                            filter: {
                                duration: 0.5,
                                ease: "easeOut",
                            },
                        }}
                    >
                        <Box
                            data-leaderboard-viewer-me={showYouLabel ? "" : undefined}
                            role={profilePath ? "button" : undefined}
                            tabIndex={profilePath ? 0 : undefined}
                            onClick={
                                profilePath
                                    ? () => history.push(profilePath)
                                    : undefined
                            }
                            onKeyDown={
                                profilePath
                                    ? (event) => {
                                          if (event.key === "Enter" || event.key === " ") {
                                              event.preventDefault();
                                              history.push(profilePath);
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                isolation: "isolate",
                                position: "relative",
                                width: {
                                    md: position.id === 1 ? "14.75rem" : "12.25rem",
                                },
                                height: style.height,
                                mt: style.mt,
                                zIndex: style.zIndex,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                border: `1px solid ${style.borderColor}`,
                                borderBottom: "none",
                                borderRadius: "8px 8px 0 0",
                                background: style.background,
                                boxShadow: style.boxShadow,
                                flexShrink: 0,
                                transition: "transform 240ms cubic-bezier(0.22,1,0.36,1), box-shadow 240ms ease, border-color 240ms ease",
                                [PODIUM_COMPACT_MQ]: {
                                    width: PODIUM_COMPACT_WIDTH[position.id],
                                    height: PODIUM_COMPACT_HEIGHT[position.id],
                                },
                                ...(profilePath
                                    ? {
                                        cursor: "pointer",
                                        "&:hover [data-podium-overlay]": {
                                            opacity: 0.82,
                                        },
                                        "&:hover [data-podium-avatar]": {
                                            transform: "translateY(-2px) scale(1.045)",
                                        },
                                        "&:hover": {
                                            transform: "translateY(-6px) scale(1.012)",
                                            boxShadow: style.hoverShadow,
                                        },
                                        "&:focus-visible": {
                                            outline: `2px solid ${alpha(style.accent, 0.75)}`,
                                            outlineOffset: "4px",
                                        },
                                    }
                                    : {}),
                            }}
                        >

                            {/* Position */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "-1.15rem",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "2.3rem",
                                    height: "2.3rem",
                                    borderRadius: "50%",
                                    background: style.placeBackground,
                                    border: style.placeBorder,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: style.placeShadow,
                                    zIndex: 5,
                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        inset: "3px",
                                        borderRadius: "50%",
                                        border: "1px solid rgba(255,255,255,0.09)",
                                        boxShadow: "inset 0 1px 3px rgba(255,255,255,0.04), inset 0 -2px 4px rgba(0,0,0,0.5)",
                                        pointerEvents: "none",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "'Orbitron', sans-serif",
                                        position: "relative",
                                        zIndex: 1,
                                        fontSize: "0.78rem",
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        letterSpacing: "-0.02em",
                                        color: style.placeText,
                                        textShadow: "0 1px 0 rgba(255,255,255,0.56), 0 -1px 0 rgba(0,0,0,0.18), 0 2px 3px rgba(0,0,0,0.34)",
                                    }}
                                >
                                    {position.label}
                                </Typography>
                            </Box>

                            {/* Overlay */}
                            <Box
                                data-podium-overlay
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    background: style.overlay,
                                    borderRadius: "8px 8px 0 0",
                                    zIndex: 1,
                                    transition: "opacity 240ms ease",
                                    pointerEvents: "none",
                                }}
                            />
                            <Box
                                aria-hidden
                                data-podium-hud
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1,
                                    backgroundImage: `repeating-linear-gradient(0deg, transparent 0, transparent 5px, ${alpha(style.accent, 0.035)} 6px), linear-gradient(90deg, transparent 0%, ${alpha(style.accent, 0.05)} 50%, transparent 100%)`,
                                    maskImage: "linear-gradient(180deg, transparent 8%, #000 42%, #000 100%)",
                                    pointerEvents: "none",
                                }}
                            />
                            <Box
                                component="svg"
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                aria-hidden
                                sx={{
                                    position: "absolute",
                                    inset: "0.38rem",
                                    width: "calc(100% - 0.76rem)",
                                    height: "calc(100% - 0.76rem)",
                                    zIndex: 1,
                                    overflow: "visible",
                                    opacity: position.id === 1 ? 0.94 : 0.84,
                                    filter: `drop-shadow(0 0 9px ${alpha(style.accent, position.id === 1 ? 0.28 : 0.2)})`,
                                    pointerEvents: "none",
                                }}
                            >
                                <path
                                    d="M 7 1 H 90 L 99 10 V 90 L 90 99 H 10 L 1 90 V 10 Z"
                                    fill="none"
                                    stroke={alpha(style.accent, position.id === 1 ? 0.5 : 0.42)}
                                    strokeWidth="1"
                                    vectorEffect="non-scaling-stroke"
                                />
                                <path
                                    d="M 7 1 H 30 M 70 99 H 90 L 99 90 V 72"
                                    fill="none"
                                    stroke={alpha(style.accent, position.id === 1 ? 0.96 : 0.88)}
                                    strokeWidth={position.id === 1 ? "2" : "1.5"}
                                    strokeLinecap="square"
                                    vectorEffect="non-scaling-stroke"
                                />
                                <path
                                    d="M 1 20 V 10 L 7 1 M 99 80 V 90 L 90 99"
                                    fill="none"
                                    stroke={alpha("#FFFFFF", position.id === 1 ? 0.16 : 0.12)}
                                    strokeWidth="1"
                                    vectorEffect="non-scaling-stroke"
                                />
                            </Box>
                            {/* Content */}
                            <Box
                                sx={{
                                    zIndex: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "0.42rem",
                                    width: "calc(100% - 1.4rem)",
                                    px: "0.8rem",
                                    pt: "0.55rem",
                                    pb: "0.8rem",
                                    boxSizing: "border-box",
                                    textAlign: "center",
                                }}
                            >
                                {avatarSrc ? (
                                        <Box
                                            data-podium-avatar
                                            sx={{
                                                position: "relative",
                                                width: PODIUM_AVATAR_BOX_SIZE[position.id],
                                                height: PODIUM_AVATAR_BOX_SIZE[position.id],
                                                boxSizing: "border-box",
                                                borderRadius: "50%",
                                                boxShadow: "0 16px 34px rgba(0,0,0,0.58)",
                                                margin: "0.5rem auto",
                                                transition: "transform 240ms ease, box-shadow 240ms ease",
                                                [PODIUM_COMPACT_MQ]: {
                                                    width: PODIUM_COMPACT_AVATAR[position.id],
                                                    height: PODIUM_COMPACT_AVATAR[position.id],
                                                },
                                            }}
                                        >
                                        <Avatar
                                            src={avatarSrc}
                                            alt={row?.nickname || ""}
                                            sx={{
                                                position: "relative",
                                                zIndex: 1,
                                                width: "100%",
                                                height: "100%",
                                                margin: 0,
                                            }}
                                        />
                                    </Box>
                                ) : null}
                                {row?.nickname ? (
                                    <Typography
                                        sx={{
                                            minWidth: 0,
                                            maxWidth: "100%",
                                            color: colors.text.primary,
                                            fontSize: { xs: "1rem", xl: "1.15rem" },
                                            textShadow: "0 2px 10px rgba(0,0,0,0.72)",
                                            fontWeight: 750,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            letterSpacing: "0.02em",
                                            textAlign: "center",
                                        }}
                                    >
                                        {row.nickname}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    </motion.div>
                );
            })}
        </Box>
        </>
    );
}
